<template>
  <div class="d-flex flex-column w-100">
    <div class="map-item-filter">
      <!-- Info box -->
      <div
        class="col-sm-12 nopads vehicle-list"
        :class="{ list_closed: !listOpen }"
      >
        <span
          class="vehicle-list-title"
          @click.stop="toggleList"
        >
          {{ $t('sports.skiing_tracks') }}
        </span>
        <div
          class="vehicle-list-caret"
          :class="{ caret_open: listOpen }"
          @click.stop="toggleList"
        >
          <font-awesome-icon icon="caret-down" />
        </div>
        <div
          v-if="listOpen"
          class="col-sm-12 nopads vehicle-list-container p-2"
        >
          <div class="badge-filters__wrapper">
            <div class="badge-filters">
                <div
                    v-for="item in routeTypeOptions"
                    :key="item.value"
                    class="badge"
                    :class="routeType === item.value ? 'bg-primary' : 'bg-light text-dark'"
                    style="padding: .5em 1em; cursor: pointer;"
                    @click="routeType = item.value"
                >
                    {{ item.text }}
                </div>
            </div>
          </div>
            <select
                id="citySelect"
                class="form-control form-control-sm"
                v-model="selectedCity"
            >
                <option disabled value="">{{ $t('common.select_a_city') }}</option>
                <option v-for="city in cityOptions" :key="city.value" :value="city.value">
                    {{ city.text }}
                </option>
            </select>
        </div>
        <!-- Work records -->
      </div>
    </div>
    <map-ski-track-work-history
      v-if="selectedCity"
      ref="workHistory"
      class="nopads"
      style="pointer-events: all"
      @track-clicked="trackClicked"
    />
    <ski-track-work-editor
      v-if="showWorkEditor"
      class="map-item-filter"
      :user="user"
      :track="selectedTrack"
      :city="selectedCity"
      @close="showWorkEditor = false"
      @close-and-update="updateWorkRecords"
    />
    <!-- Spinner -->
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {mapHelper} from '../mixins/MapMixin'
import {sportHelper} from '../mixins/SportMixin'
import {timeUtils} from '../mixins/TimeUtils'
import MapSkiTrackWorkHistory from "./MapSkiTrackWorkHistory"
import SkiTrackWorkEditor from "./SkiTrackWorkEditor"
import {inject} from "vue";

export default {
    name: 'MapSkiing',
    components: {SkiTrackWorkEditor, MapSkiTrackWorkHistory},
    mixins: [restApi, mapHelper, sportHelper, timeUtils],
    props: {
        map: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            fetching: false,
            showWorkEditor: false,
            fromTime: null,
            tracks: [],
            polyLines: [],
            listOpen: true,
            selectedCity: undefined,
            routeType: null,
            selectedTrack: null
        }
    },

    computed: {
        routeTypeOptions() {
            return [
                { text: this.$t('sports.tracks_all'), value: null },
                { text: this.$t('sports.skiing_track'), value: this.TRACK_SKIING },
                { text: this.$t('sports.multipurpose'), value: this.TRACK_MULTIPURPOSE },
                { text: this.$t('sports.tracks_cycling'), value: this.TRACK_CYCLING },
                { text: this.$t('sports.tracks_offroad_cycling'), value: this.TRACK_CYCLING_OFFROAD },
                { text: this.$t('sports.tracks_walking'), value: this.TRACK_WALKING },
                { text: this.$t('sports.tracks_jogging'), value: this.TRACK_JOGGING },
                { text: this.$t('sports.tracks_hiking'), value: this.TRACK_HIKING },
                { text: this.$t('sports.tracks_snowmobile'), value: this.TRACK_SNOWMOBILE },
                { text: this.$t('sports.tracks_snowmobile_path'), value: this.TRACK_SNOWMOBILE_PATH },
            ]
        }
    },
    watch: {
        selectedCity() {
            this.updateMapSettings({ buttons: { skiing: { city: this.selectedCity } } });
            this.saveMapSettings();
            this.onParamsChanged()
        },
        routeType() {
          this.onParamsChanged()
        }
    },

    async mounted() {
        if (this.selectedCity === undefined) {
            this.selectedCity = await this.getMapSettingsIn(['buttons','skiing','city'], null);
        }
        this.fromTime = new Date()
        // TODO - Allow user to choose time range
        this.fromTime = this.fromTime.setHours(this.fromTime.getHours() - 48)
        this.routeType = this.TRACK_SKIING
    },

    beforeUnmount: function () {
        this.hideTrackInfo()
        this.hideTracks()
        this.hideSnapshot()
    },

    methods: {
        onParamsChanged()  {
            if (this.selectedCity != null) {
                this.fetchTracks()
                this.fetchRecords()
                this.selectedTrack = null
            } else {
                this.hideTracks()
                this.hideSnapshot()
            }
        },

        fetchTracks: function () {
            this.loading = true
            this.hideTracks()
            this.restFetchParams(this.sportTrackUrl, {
                city: this.selectedCity,
                type: this.routeType
            }, this.handleResponse);
        },

        fetchTracksById(trackId) {
            this.loading = true
            this.hideTracks()
            this.restFetch(this.skiingTrackUrl + '/' + trackId, this.handleResponseAndFocus, this.handleError)
        },

        handleResponse: function (response) {
            if (response && response.data) {
                this.tracks = response.data
                this.showTracks(response.data)
            }
            this.loading = false
        },

        handleResponseAndFocus: function (response) {
            if (response && response.data) {
                this.tracks = response.data
                this.showTracks(response.data)
                this.map.zoomToPosition(response.data[0].geometry[0]['y'], response.data[0].geometry[0]['x'])
            }
            this.loading = false
        },

        hideTracks: function () {
            this.polyLines.forEach(line => {
                this.map.removePolyline(line, this.SKI_TRACK)
            })
        },

        showTracks: function (tracks) {
            tracks.forEach(item => {
                if (item.geometry && item.geometry.length > 1) {
                    this.drawSkiingTrack(item.id, item.typeName, item.geometry)
                    this.polyLines.push(item.id)
                }
            })
        },

        drawSkiingTrack: function (id, type, coords) {
            let color = this.getTypeColor(type)
            this.map.drawPolyLine(id, this.SKI_TRACK, coords, color, false, true, 3)
        },

        onPolylineTap: function (data, coords) {
           this.showTrackInfo(data.id, coords)
        },

        showTrackInfo: function (id, coords) {
            this.hideTrackInfo()
            let track = this.tracks.find(item => parseInt(item.id) === parseInt(id))
            if (track) {
                let data ={}
                data[this.$t('sports.route')] = track.routeName
                if (!coords) {
                    coords = {
                        lat: track.geometry[0].y,
                        lng: track.geometry[0].x
                    }
                }
                this.map.showInfoBubbleWithAction(track.id, track.typeName, data, coords.lat, coords.lng,
                    this.$t('areas.add_work_record'), this.addWork)
                this.visibleBubble = id
                this.selectedTrack = track
            }
        },

        hideTrackInfo: function () {
            if (this.visibleBubble) {
                this.map.hidePolylineBubble(this.visibleBubble, this.SKI_TRACK)
                this.selectedTrack = null
            }
        },

        getTypeColor: function (type) {
            switch (parseInt(type)) {
                case this.SKI_FREESTYLE:
                    return '#52f0ff'
                case this.SKI_CLASSIC:
                    return '#5148ff'
                case this.SKI_LIGHTS:
                    return '#ffbd13'
                case this.SKI_COMPETITION:
                    return '#e454ff'
            }
            return '#2d3b38'
        },

        toggleList: function () {
            this.listOpen = !this.listOpen
        },

        // Work history
        fetchRecords: function () {
            if (!this.fetching) {
                this.fetching = true
                this.loading = true
                this.hideSnapshot()
                let params = {
                    city: this.selectedCity,
                    from: this.localTimeToUtcTime(this.fromTime)
                }
                if (this.routeType) {
                    params.type = this.routeType
                }
                this.restFetchParams(this.skiTrackRecordsUrl, params, this.handleWorkResponse)
            }
        },

        handleWorkResponse: function (response) {
            this.loading = false
            this.fetching = false
            if (response) {
                this.results = response.data
                this.showSnapshot()
            }
        },

        hideSnapshot: function () {
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.polyLines.forEach(line => {
                this.map.removePolyline(line, this.WORK_TRACE)
            })
        },

        showSnapshot: function () {
            this.results.forEach(item => {
                let workCount = item.work_records.length;
                item.work_records.forEach(work => {
                    this.drawWorkTrace(work.id, item.geometry, work.color, 6 + workCount, this.getTransparency(work.age)); // Default linewidth is 3
                    this.polyLines.push(work.id)
                    workCount--
                })
            })
        },

        drawWorkTrace: function (id, coords, color, width, transparency) {
            this.map.drawPolyLine(id, this.WORK_TRACE, coords, color, false, false, width, transparency, false, false, null, true)
        },

        getTransparency: function (age) {
            let maxAge = Math.round((Date.now() - this.fromTime) / 1000 / 60)
            if (age > maxAge) {
                return 0.1;
            }
            // Scale age to 0-1
            return 1 - (age / maxAge)
        },

        addWork: function () {
            if (this.selectedTrack) {
                this.showWorkEditor = true
            } else {
                this.showAlert(this.$t('sports.select_track_first'))
            }
        },

        trackClicked(item) {
            this.fetchTracksById(item.track_id)
        },

        updateWorkRecords() {
            this.$refs.workHistory.refreshHistory()
            this.fetchRecords()
            this.showWorkEditor = false
        },

        getTracks() {
            return this.tracks
        }

    }
}
</script>
